import * as React from 'react';

import { Button, Input, Label, Select, Text } from '@/components/ui';

// DataGridFilterPopover — body of the per-column filter popover. The
// popover surface, focus and outside-click are handled by the parent
// Popover; this component owns only the form widgets for each filter
// type and the "Clear" affordance.
//
// We read / write the filter value via TanStack's column API:
//   column.getFilterValue()
//   column.setFilterValue(value)
//
// Empty / cleared filter values are normalized to `undefined` so the
// URL serializer drops the param and the header badge falls back to
// the inactive state.
//
// Auto-close behaviour (caller passes `onClose`):
//   - `clear` → close (action complete, nothing left to do)
//   - every other interaction stays open. `select` is always multi-capable
//     (one or many) so a single pick is not "done" by itself; `text`,
//     `numberRange` and `dateRange` are likewise incremental.
// The user dismisses the popover by clicking outside or pressing Escape.

const isFilterEmpty = value =>
    value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && (value.from ?? '') === '' && (value.to ?? '') === '');

const DataGridFilterPopover = ({ column, onClose }) => {
    const meta = column.columnDef.meta?.filter;
    const filterValue = column.getFilterValue();

    if (!meta) {
        return null;
    }

    const setValue = nextValue => {
        if (isFilterEmpty(nextValue)) {
            column.setFilterValue(undefined);
        } else {
            column.setFilterValue(nextValue);
        }
    };

    const clear = () => {
        column.setFilterValue(undefined);
        onClose?.();
    };
    const hasValue = !isFilterEmpty(filterValue);
    const placeholder = meta.placeholder ?? i18next.t('datagrid.filter.placeholder', 'Filter…');

    // Per-filter "footer-left" caption — only set for select filters that
    // currently have one or more selected options. Sits left of the global
    // Clear button. Other filter types leave it null.
    let footerCaption = null;

    const body = (() => {
        switch (meta.type) {
            case 'text':
                // `clearable={false}` so the Input doesn't paint its own
                // inline clear `×` — the popover's bottom Clear handles
                // every filter type uniformly. The magnifying-glass icon
                // on the left stays so the input still reads as a search.
                return (
                    <Input
                        autoFocus
                        clearable={false}
                        onChange={event => setValue(event.target.value)}
                        placeholder={placeholder}
                        size="small"
                        type="search"
                        value={filterValue ?? ''}
                    />
                );

            case 'select': {
                // Filter value is always an array — comma-separated string
                // when restored from the URL, real array when set via the
                // widget. Normalize once so the controlled `value` for the
                // Select primitive is `Array<Option>`.
                const selectedValues = Array.isArray(filterValue)
                    ? filterValue.map(String)
                    : typeof filterValue === 'string' && filterValue.length > 0
                        ? filterValue.split(',')
                        : [];
                const options = meta.options ?? [];
                const selectedOptions = options.filter(option => selectedValues.includes(String(option.value)));

                if (selectedOptions.length > 0) {
                    footerCaption = (
                        <Text tone="muted" variant="caption">
                            {i18next.t('datagrid.filter.selected_of_total', {
                                defaultValue: '{{count}} of {{total}} selected',
                                count: selectedOptions.length,
                                total: options.length
                            })}
                        </Text>
                    );
                }

                return (
                    <Select
                        // No `clearable` on the Select — the popover's
                        // global Clear button below handles emptying the
                        // filter for every type uniformly.
                        // The popover already owns its own floating surface;
                        // letting the Select portal its menu out would paint
                        // the option list as a disconnected box. `inlineMenu`
                        // anchors the menu absolutely under the control so
                        // visually it reads as part of this popover.
                        inlineMenu
                        isMulti
                        onChange={picked => {
                            const next = (picked ?? []).map(option => option.value);
                            setValue(next);
                        }}
                        options={options}
                        placeholder={placeholder}
                        // Filter columns typically carry every distinct
                        // value in the dataset; once you have more than a
                        // handful, scrolling to the one you want is slow.
                        // Enable the inline search so the user can type to
                        // narrow the list.
                        search
                        size="small"
                        value={selectedOptions}
                    />
                );
            }

            case 'numberRange':
            case 'dateRange': {
                // `type="date"` on Input is the WiseMetering custom — text
                // under the hood, character-filtered to digits + dashes,
                // capped at 10 chars (yyyy-mm-dd length). Not the native
                // `<input type=date>` whose displayed format follows the
                // browser/OS locale. The filterDateRange strategy ignores
                // endpoints that don't match yyyy-mm-dd so partial input
                // doesn't wipe the dataset mid-keystroke.
                //
                // `clearable={false}` because the popover already owns its
                // own bottom Clear button — avoids two redundant clear
                // affordances inside the same surface (same reasoning as
                // the text-filter input above).
                const inputType = meta.type === 'dateRange' ? 'date' : 'number';
                const range = typeof filterValue === 'object' && filterValue !== null && !Array.isArray(filterValue)
                    ? filterValue
                    : typeof filterValue === 'string'
                        ? { from: filterValue.split('..')[0] ?? '', to: filterValue.split('..')[1] ?? '' }
                        : { from: '', to: '' };

                return (
                    <div className="tw-flex tw-flex-col tw-gap-2">
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <Label htmlFor={`filter-${column.id}-from`}>
                                {i18next.t('datagrid.filter.from', 'From')}
                            </Label>
                            <Input
                                clearable={false}
                                id={`filter-${column.id}-from`}
                                onChange={event => setValue({ ...range, from: event.target.value })}
                                size="small"
                                type={inputType}
                                value={range.from ?? ''}
                            />
                        </div>
                        <div className="tw-flex tw-flex-col tw-gap-1">
                            <Label htmlFor={`filter-${column.id}-to`}>
                                {i18next.t('datagrid.filter.to', 'To')}
                            </Label>
                            <Input
                                clearable={false}
                                id={`filter-${column.id}-to`}
                                onChange={event => setValue({ ...range, to: event.target.value })}
                                size="small"
                                type={inputType}
                                value={range.to ?? ''}
                            />
                        </div>
                    </div>
                );
            }

            default:
                return null;
        }
    })();

    return (
        <div className="tw-flex tw-w-datagrid-filter-popover tw-flex-col tw-gap-2">
            <Text tone="muted" variant="overline">
                {column.columnDef.meta?.label ?? column.id}
            </Text>
            {body}
            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                {footerCaption ? (
                    <div className="tw-mr-auto">{footerCaption}</div>
                ) : null}
                <Button
                    appearance="ghost"
                    disabled={!hasValue}
                    onClick={clear}
                    size="small"
                    tone="neutral"
                >
                    {i18next.t('datagrid.filter.clear', 'Clear')}
                </Button>
            </div>
        </div>
    );
};

export default DataGridFilterPopover;
